/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/settingsHelpers.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Settings normalization and transformation utilities.
────────────────────────────────────────────────────────────────*/

import { DisaSettings, DisaSettingsPayload } from '../types';
import { DEFAULT_SETTINGS } from './formDefaults';

/**
 * Sanitize schedule window value
 */
function sanitizeScheduleWindow(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() || null;
  return JSON.stringify(value);
}

/**
 * Normalize settings from API response
 */
export function normalizeSettings(incoming: unknown): DisaSettings {
  const raw = (typeof incoming === 'object' && incoming !== null ? incoming : {}) as Record<string, unknown>;

  return {
    dialPrefix: String(raw.dial_prefix ?? raw.dialPrefix ?? DEFAULT_SETTINGS.dialPrefix).slice(0, 16),
    callerIdName: String(raw.caller_id_name ?? raw.callerIdName ?? DEFAULT_SETTINGS.callerIdName).slice(0, 128),
    callerIdNumber: String(raw.caller_id_number ?? raw.callerIdNumber ?? DEFAULT_SETTINGS.callerIdNumber).slice(0, 32),
    inheritCallerId: Boolean(raw.inherit_caller_id ?? raw.inheritCallerId ?? DEFAULT_SETTINGS.inheritCallerId),
    greeting: String(raw.greeting ?? DEFAULT_SETTINGS.greeting ?? '').slice(0, 255),
    invalidPinSound: String(raw.invalid_pin_sound ?? raw.invalidPinSound ?? DEFAULT_SETTINGS.invalidPinSound).slice(0, 255),
    timeoutSound: String(raw.timeout_sound ?? raw.timeoutSound ?? DEFAULT_SETTINGS.timeoutSound).slice(0, 255),
    responseTimeout: Math.min(60, Math.max(1, Number(raw.response_timeout ?? raw.responseTimeout ?? DEFAULT_SETTINGS.responseTimeout))),
    digitTimeout: Math.min(10, Math.max(1, Number(raw.digit_timeout ?? raw.digitTimeout ?? DEFAULT_SETTINGS.digitTimeout))),
    maxCallDuration: Math.min(3600, Math.max(60, Number(raw.max_call_duration ?? raw.maxCallDuration ?? DEFAULT_SETTINGS.maxCallDuration))),
    maxAttempts: Math.min(5, Math.max(1, Number(raw.max_attempts ?? raw.maxAttempts ?? DEFAULT_SETTINGS.maxAttempts))),
    hangupOnFail: Boolean(raw.hangup_on_fail ?? raw.hangupOnFail ?? DEFAULT_SETTINGS.hangupOnFail),
    recordCall: Boolean(raw.record_call ?? raw.recordCall ?? DEFAULT_SETTINGS.recordCall),
    logAccess: Boolean(raw.log_access ?? raw.logAccess ?? DEFAULT_SETTINGS.logAccess),
    allowLocal: Boolean(raw.allow_local ?? raw.allowLocal ?? DEFAULT_SETTINGS.allowLocal),
    allowInternational: Boolean(raw.allow_international ?? raw.allowInternational ?? DEFAULT_SETTINGS.allowInternational),
    ipRestriction: Array.isArray(raw.ip_restriction ?? raw.ipRestriction)
      ? Array.from(
          new Set(
            ((raw.ip_restriction ?? raw.ipRestriction) as unknown[]).map((value: unknown) => String(value).slice(0, 64)).filter(Boolean)
          )
        )
      : [],
    scheduleWindow: sanitizeScheduleWindow(raw.schedule_window ?? raw.scheduleWindow),
    retryOnFail: Boolean(raw.retry_on_fail ?? raw.retryOnFail ?? DEFAULT_SETTINGS.retryOnFail),
    notifyOnAccess: String(raw.notify_on_access ?? raw.notifyOnAccess ?? DEFAULT_SETTINGS.notifyOnAccess).slice(0, 255)
  };
}

/**
 * Build settings payload for API (convert to snake_case)
 */
export function buildPayloadSettings(settings: DisaSettings): DisaSettingsPayload {
  return {
    dial_prefix: settings.dialPrefix || null,
    caller_id_name: settings.callerIdName || null,
    caller_id_number: settings.callerIdNumber || null,
    inherit_caller_id: settings.inheritCallerId,
    greeting: settings.greeting || null,
    invalid_pin_sound: settings.invalidPinSound,
    timeout_sound: settings.timeoutSound,
    response_timeout: settings.responseTimeout,
    digit_timeout: settings.digitTimeout,
    max_call_duration: settings.maxCallDuration,
    max_attempts: settings.maxAttempts,
    hangup_on_fail: settings.hangupOnFail,
    record_call: settings.recordCall,
    log_access: settings.logAccess,
    allow_local: settings.allowLocal,
    allow_international: settings.allowInternational,
    ip_restriction: settings.ipRestriction,
    schedule_window: settings.scheduleWindow,
    retry_on_fail: settings.retryOnFail,
    notify_on_access: settings.notifyOnAccess || null
  };
}

/**
 * Generate context from name
 */
export function generateContextFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 50);
}

